import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  usePropertyDirectory,
  useUpdatePropertyDirectory,
  type LinkedLocation,
  type PropertyDirectoryPatch,
  type PropertyDirectoryRow,
} from '../hooks/usePropertyDirectory';
import { useRelinkPropertyDirectory } from '../hooks/useUnifiSites';
import { formatDateTime } from '../lib/formatters';

/**
 * Property directory — the registry of managed properties that UniFi hosts,
 * Mission Control sites, Auvik domains and Learn links hang off.
 * Locations join a property through `locations.hierarchy->>'property_code'`.
 */

const BTN =
  'inline-flex items-center justify-center min-h-[44px] md:min-h-[36px] px-3 rounded-md text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50';
const INPUT =
  'w-full min-h-[44px] md:min-h-[40px] rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary';

type Feedback = { tone: 'success' | 'error'; text: string };

export function PropertyDirectoryPage() {
  const { data, isLoading, isError } = usePropertyDirectory();
  const relink = useRelinkPropertyDirectory();
  const [search, setSearch] = useState('');
  const [relinkMessage, setRelinkMessage] = useState<Feedback | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const properties = useMemo(() => {
    const list = data?.properties ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) =>
      [p.code, p.name, p.brand, p.city, p.state, p.unifi_host_id, p.mission_control_site_id, ...p.aliases]
        .filter((v): v is string => Boolean(v))
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [data?.properties, search]);

  const runRelink = async () => {
    setRelinkMessage(null);
    try {
      const linked = await relink.mutateAsync();
      setRelinkMessage({ tone: 'success', text: `Relink finished: ${linked} location${linked === 1 ? '' : 's'} received a property code.` });
    } catch (e) {
      setRelinkMessage({ tone: 'error', text: `Relink failed: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const total = data?.properties.length ?? 0;
  const linkedCount = data ? Array.from(data.locationsByCode.values()).reduce((sum, list) => sum + list.length, 0) : 0;
  const unlinked = data?.unlinkedLocations ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-text">Property Directory</h1>
          <p className="text-sm text-text-muted mt-1">
            {isLoading ? 'Loading…' : `${total} propert${total === 1 ? 'y' : 'ies'} · ${linkedCount} linked location${linkedCount === 1 ? '' : 's'} · ${unlinked.length} without a property code`}
          </p>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button type="button" onClick={runRelink} disabled={relink.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
            {relink.isPending ? 'Relinking…' : 'Relink locations'}
          </button>
          <p className="text-[11px] text-text-faint sm:text-right max-w-xs">Matches location names and aliases against the directory and stamps the property code.</p>
        </div>
      </div>
      {relinkMessage && (
        <p role={relinkMessage.tone === 'error' ? 'alert' : 'status'} className={`text-sm break-words ${relinkMessage.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {relinkMessage.text}
        </p>
      )}

      <label className="block sm:w-80">
        <span className="sr-only">Search properties</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search code, name, brand, city, host id or alias"
          className={INPUT}
        />
      </label>

      {isError && (
        <p role="alert" className="rounded-lg border border-border bg-surface p-4 text-sm text-notification">Could not load the property directory. Refresh to try again.</p>
      )}

      {!isLoading && !isError && properties.length === 0 && (
        <p className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
          {total === 0 ? 'The property directory is empty.' : 'No properties match your search.'}
        </p>
      )}

      {/* Mobile: cards */}
      <div className="md:hidden space-y-2">
        {properties.map((p) => (
          <div key={p.code} className="rounded-lg border border-border bg-surface">
            <button
              type="button"
              onClick={() => setExpanded(expanded === p.code ? null : p.code)}
              aria-expanded={expanded === p.code}
              className="w-full text-left p-3 min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text truncate">{p.name}</p>
                  <p className="text-xs text-text-muted">{p.code}{p.brand ? ` · ${p.brand}` : ''}{p.city || p.state ? ` · ${[p.city, p.state].filter(Boolean).join(', ')}` : ''}</p>
                </div>
                <span className="text-xs text-text-faint whitespace-nowrap">{(data?.locationsByCode.get(p.code)?.length ?? 0)} loc.</span>
              </div>
              <p className="text-[11px] text-text-faint mt-1 break-all">
                UniFi {p.unifi_host_id ?? '—'} · MC {p.mission_control_site_id ?? '—'}
              </p>
            </button>
            {expanded === p.code && (
              <div className="border-t border-border p-3 space-y-3">
                <LinkedLocations locations={data?.locationsByCode.get(p.code) ?? []} />
                <PropertyEditor property={p} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      {properties.length > 0 && (
        <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-3 text-left text-text-muted">
              <tr>
                <th className="px-3 py-2.5 font-medium">Code</th>
                <th className="px-3 py-2.5 font-medium">Property</th>
                <th className="px-3 py-2.5 font-medium">Brand</th>
                <th className="px-3 py-2.5 font-medium">City / State</th>
                <th className="px-3 py-2.5 font-medium">UniFi host</th>
                <th className="px-3 py-2.5 font-medium">Mission Control</th>
                <th className="px-3 py-2.5 font-medium text-center">Locations</th>
                <th className="px-3 py-2.5 font-medium text-right">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider bg-surface">
              {properties.map((p) => {
                const linked = data?.locationsByCode.get(p.code) ?? [];
                const open = expanded === p.code;
                return (
                  <PropertyRows key={p.code} property={p} linked={linked} open={open} onToggle={() => setExpanded(open ? null : p.code)} />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {unlinked.length > 0 && (
        <section className="rounded-lg border border-border bg-surface" aria-labelledby="unlinked-heading">
          <header className="px-4 py-3 border-b border-border">
            <h2 id="unlinked-heading" className="text-sm font-semibold text-text">Locations without a property code ({unlinked.length})</h2>
            <p className="text-xs text-text-muted">These sites cannot be mapped to UniFi hosts through the directory. Add their name as an alias on the right property, then relink.</p>
          </header>
          <ul className="divide-y divide-border">
            {unlinked.map((l) => (
              <li key={l.id} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm">
                <Link to={`/locations/${l.id}`} className="text-primary hover:underline">{l.name}</Link>
                <span className="text-xs text-text-muted">{l.customer_name ?? 'Unknown customer'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function PropertyRows({ property: p, linked, open, onToggle }: { property: PropertyDirectoryRow; linked: LinkedLocation[]; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="hover:bg-surface-strong">
        <td className="px-3 py-2.5 font-mono text-xs text-text">{p.code}</td>
        <td className="px-3 py-2.5 font-medium text-text">{p.name}</td>
        <td className="px-3 py-2.5 text-text-muted">{p.brand ?? '—'}</td>
        <td className="px-3 py-2.5 text-text-muted">{[p.city, p.state].filter(Boolean).join(', ') || '—'}</td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-muted break-all max-w-[12rem]">{p.unifi_host_id ?? '—'}</td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-muted">{p.mission_control_site_id ?? '—'}</td>
        <td className="px-3 py-2.5 text-center text-text">{linked.length}</td>
        <td className="px-3 py-2.5 text-right">
          <button type="button" onClick={onToggle} aria-expanded={open} className="text-xs text-primary hover:underline min-h-[36px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
            {open ? 'Close' : 'Edit'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={8} className="px-3 py-4 bg-surface-2">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-4">
              <PropertyEditor property={p} />
              <LinkedLocations locations={linked} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function LinkedLocations({ locations }: { locations: LinkedLocation[] }) {
  return (
    <div>
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">Linked locations</p>
      {locations.length === 0 ? (
        <p className="text-xs text-text-muted">No location carries this property code yet. Run “Relink locations” after adding aliases.</p>
      ) : (
        <ul className="space-y-1">
          {locations.map((l) => (
            <li key={l.id} className="text-xs">
              <Link to={`/locations/${l.id}`} className="text-primary hover:underline">{l.name}</Link>
              <span className="text-text-muted"> · {l.customer_name ?? 'Unknown customer'}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface EditorForm {
  name: string;
  brand: string;
  city: string;
  state: string;
  lat: string;
  lng: string;
  mission_control_site_id: string;
  unifi_host_id: string;
  auvik_domain_prefix: string;
  learn_url: string;
  engineering_url: string;
  ordering_url: string;
  rankings_url: string;
  aliases: string;
}

function toForm(p: PropertyDirectoryRow): EditorForm {
  return {
    name: p.name,
    brand: p.brand ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    lat: p.lat === null ? '' : String(p.lat),
    lng: p.lng === null ? '' : String(p.lng),
    mission_control_site_id: p.mission_control_site_id ?? '',
    unifi_host_id: p.unifi_host_id ?? '',
    auvik_domain_prefix: p.auvik_domain_prefix ?? '',
    learn_url: p.learn_url ?? '',
    engineering_url: p.engineering_url ?? '',
    ordering_url: p.ordering_url ?? '',
    rankings_url: p.rankings_url ?? '',
    aliases: p.aliases.join(', '),
  };
}

function nullable(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

function toPatch(form: EditorForm): { patch: PropertyDirectoryPatch; error: string | null } {
  const name = form.name.trim();
  if (!name) return { patch: {}, error: 'Name is required.' };
  const lat = form.lat.trim() === '' ? null : Number(form.lat);
  const lng = form.lng.trim() === '' ? null : Number(form.lng);
  if (lat !== null && (!Number.isFinite(lat) || lat < -90 || lat > 90)) return { patch: {}, error: 'Latitude must be a number between -90 and 90.' };
  if (lng !== null && (!Number.isFinite(lng) || lng < -180 || lng > 180)) return { patch: {}, error: 'Longitude must be a number between -180 and 180.' };
  for (const key of ['learn_url', 'engineering_url', 'ordering_url', 'rankings_url'] as const) {
    const v = form[key].trim();
    if (v && !/^https?:\/\//i.test(v)) return { patch: {}, error: `${key.replace(/_/g, ' ')} must start with http:// or https://.` };
  }
  return {
    error: null,
    patch: {
      name,
      brand: nullable(form.brand),
      city: nullable(form.city),
      state: nullable(form.state),
      lat,
      lng,
      mission_control_site_id: nullable(form.mission_control_site_id),
      unifi_host_id: nullable(form.unifi_host_id),
      auvik_domain_prefix: nullable(form.auvik_domain_prefix),
      learn_url: nullable(form.learn_url),
      engineering_url: nullable(form.engineering_url),
      ordering_url: nullable(form.ordering_url),
      rankings_url: nullable(form.rankings_url),
      aliases: form.aliases.split(',').map((a) => a.trim()).filter(Boolean),
    },
  };
}

function PropertyEditor({ property }: { property: PropertyDirectoryRow }) {
  const update = useUpdatePropertyDirectory();
  const [form, setForm] = useState<EditorForm>(() => toForm(property));
  const [message, setMessage] = useState<Feedback | null>(null);

  const set = (key: keyof EditorForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setMessage(null);
  };

  const save = async () => {
    const { patch, error } = toPatch(form);
    if (error) {
      setMessage({ tone: 'error', text: error });
      return;
    }
    try {
      await update.mutateAsync({ code: property.code, patch });
      setMessage({ tone: 'success', text: 'Saved.' });
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not save: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const field = (label: string, key: keyof EditorForm, type: 'text' | 'url' = 'text', hint?: string) => (
    <label className="block">
      <span className="block text-xs text-text-muted mb-1">{label}</span>
      <input type={type} value={form[key]} onChange={set(key)} className={INPUT} inputMode={key === 'lat' || key === 'lng' ? 'decimal' : undefined} />
      {hint && <span className="block text-[11px] text-text-faint mt-0.5">{hint}</span>}
    </label>
  );

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      aria-label={`Edit ${property.code}`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {field('Name', 'name')}
        {field('Brand', 'brand')}
        {field('City', 'city')}
        {field('State', 'state')}
        {field('Latitude', 'lat')}
        {field('Longitude', 'lng')}
        {field('Mission Control site id', 'mission_control_site_id')}
        {field('UniFi host id', 'unifi_host_id', 'text', 'Site Manager host id; remap uses it to place UniFi sites.')}
        {field('Auvik domain prefix', 'auvik_domain_prefix')}
        {field('Learn URL', 'learn_url', 'url')}
        {field('Engineering URL', 'engineering_url', 'url')}
        {field('Ordering URL', 'ordering_url', 'url')}
        {field('Rankings URL', 'rankings_url', 'url')}
        {field('Aliases', 'aliases', 'text', 'Comma-separated alternative names used by relink.')}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={update.isPending} className={`${BTN} bg-primary text-text-inverse hover:bg-primary-hover`}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={() => { setForm(toForm(property)); setMessage(null); }} className={`${BTN} text-text-muted hover:text-text`}>
          Reset
        </button>
        <span className="text-[11px] text-text-faint">Updated {formatDateTime(property.updated_at)}</span>
      </div>
      {message && (
        <p role={message.tone === 'error' ? 'alert' : 'status'} className={`text-xs break-words ${message.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {message.text}
        </p>
      )}
    </form>
  );
}
